import { ErrorMessage } from "@hookform/error-message";
import AngleLeft from "@icons/angle-left.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/common/components/button/button";
import { InputField } from "@/common/components/form/input-field/input-field";
import { Info } from "@/common/components/info/info";
import { PageTitle } from "@/common/components/page-title/page-title";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { useLanguage } from "@/common/hooks/use-language";
import { invariant } from "@/common/utils/invariant";
import { getHarvestYears } from "@/common/utils/trading";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { storageQuery } from "../shared/api/get-storage";
import { cropsQuery, useAddCrop } from "./api";
import { Crop, CropList } from "./components/crop-list/crop-list";

const path = "crops/new";

const NewCrop = () => {
  const { id } = useParams();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(id, "Storage id is not defined");
  const queryClient = useQueryClient();
  const locale = useLanguage();
  const storage = useSuspenseQuery(storageQuery({ id, locale }));
  const { currentCrop, handleCropSelect, handleCropClear } = useCurrentCrop();
  const availableCrops = useSuspenseQuery(cropsQuery());
  const addCropMutation = useAddCrop();
  const navigate = useNavigate();

  const handleSubmit = (data: Fields) => {
    invariant(currentCrop);
    addCropMutation.mutate(
      {
        harvestYear: data.harvestYear,
        storage: {
          address: storage.data.address,
          companyId: storage.data.company.id,
          crops: {
            id: currentCrop.id,
            tons: data.tons,
          },
          label: storage.data.name,
          zip: storage.data.postalCode,
        },
        storageId: id,
      },
      {
        onSuccess: async () => {
          void queryClient.invalidateQueries();
          await navigate("../..", {
            relative: "path",
          });
        },
      },
    );
  };

  return (
    <FullscreenModalLayout
      headerButton={
        currentCrop ? (
          <button onClick={handleCropClear}>
            <AngleLeft />
          </button>
        ) : (
          <SearchParamsLink relative="path" replace to="../..">
            <AngleLeft />
          </SearchParamsLink>
        )
      }
    >
      <Spacer className="h-6" />
      <PageTitle>
        <Trans>Přidat komoditu</Trans>
      </PageTitle>
      <PropertiesList
        properties={[
          {
            label: <Trans>Adresa</Trans>,
            value: storage.data.address,
          },
          {
            label: <Trans>Farma</Trans>,
            value: storage.data.company.name,
          },
          {
            label: <Trans>Sklad</Trans>,
            value: storage.data.name,
          },
        ]}
      />
      <Spacer className="h-6" />
      {currentCrop ? (
        <CropForm
          onSubmit={
            addCropMutation.status === "pending" ? () => {} : handleSubmit
          }
        />
      ) : availableCrops.data.length > 0 ? (
        <CropList crops={availableCrops.data} onClick={handleCropSelect} />
      ) : (
        <Info>
          <Trans>Ve skladu jsou již všechny dostupné komodity</Trans>
        </Info>
      )}
    </FullscreenModalLayout>
  );
};

type Fields = {
  tons: number;
  harvestYear: number;
};

const CropForm = ({ onSubmit }: { onSubmit: (data: Fields) => void }) => {
  const form = useForm<Fields>();

  return (
    <FormProvider {...form}>
      <form id="crop-form" onSubmit={form.handleSubmit(onSubmit)}>
        <InputField<Fields>
          label={<Trans>Skladem tun</Trans>}
          name="tons"
          options={{
            min: {
              message: t`Množství musí být vyšší než 1 tuna`,
              value: 1,
            },
            required: t`Vyplňte prosím množství`,
            valueAsNumber: true,
          }}
          step="0.1"
          type="number"
        />
        <Spacer className="h-6" />
        <label className="group" htmlFor="harvestYear">
          <Trans>Rok sklizně</Trans>
          <select
            className="group-has-[[role=alert]]:border-can-russet mt-2 h-[calc(--spacing(14)+2px)] w-full rounded-lg p-4 font-bold"
            defaultValue=""
            {...form.register("harvestYear", {
              required: t`Vyplňte prosím rok sklizně`,
              valueAsNumber: true,
            })}
          >
            <option disabled value=""></option>
            {getHarvestYears().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <ErrorMessage
            errors={form.formState.errors}
            name="harvestYear"
            render={({ message }) => (
              <p
                className="text-can-russet mt-2 basis-full text-left text-xs"
                role="alert"
              >
                {message}
              </p>
            )}
          />
        </label>
      </form>
      <Button.Container>
        <Button form="crop-form" variant="primary">
          <Trans>Uložit</Trans>
        </Button>
      </Button.Container>
    </FormProvider>
  );
};

const useCurrentCrop = () => {
  const [currentCrop, setCurrentCrop] = React.useState<Crop>();

  const handleCropSelect = (crop: Crop) => {
    setCurrentCrop(crop);
  };

  const handleCropClear = () => {
    setCurrentCrop(undefined);
  };

  return { currentCrop, handleCropClear, handleCropSelect };
};

export { NewCrop, path };
